/**
 * StepPartnerExpertise.js
 *
 * An onboarding step for partners to select their areas of expertise.
 * Uses tagNodeUtils to manage the facade state of selected expertise tags.
 */
import React, { useEffect, useState } from 'react';
import TagTypes from '@/constants/TagTypes';
import useTags from '@/hooks/useTags';
import FormTemplate from '@/dialogs_common/SetupTemplate';
import Overlay from '@/forms/_common/Overlay';
import RequestError from '@/forms/_common/RequestError';
import { useOnboarding } from '@/pages/setup/account/OnboardingContext';
import {PayloadStates} from '@lore/utils';
import Loader from '@/components/Loader';
import ExpertiseSelector from '@/pages/setup/_common/ExpertiseSelector';
import TagNodeUpdateDialog from '@/pages/setup/_common/TagNodeUpdateDialog';
import { toggleFacadeTagNode, saveFacadeTagNodes, updateFacadeTagNodeDescription } from '@/pages/setup/account/tagNodeUtils';

/**
 * The partner expertise selection step in the onboarding wizard.
 *
 * @returns {JSX.Element} The rendered partner expertise step.
 */
export default function StepPartnerExpertise() {
  const { updateData, registerOnBeforeNext, setNextDisabled, data, tagNodeCreate, tagNodeUpdate, tagNodeDestroy, refetchUser, saving, error, request, loading } = useOnboarding();

  const [userExpertiseList, setUserExpertiseList] = useState(data.userExpertise ?? []);
  const [softDeletes, setSoftDeletes] = useState(data.userExpertiseSoftDeletes ?? [])
  const [editingDialog, setEditingDialog] = useState(null); // { expertise, userExpertise }
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (data.userExpertise) {
      setUserExpertiseList(data.userExpertise);
    }
  }, [data.userExpertise]);

  useEffect(() => {
    if (data.userExpertiseSoftDeletes) {
      setSoftDeletes(data.userExpertiseSoftDeletes);
    }
  }, [data.userExpertiseSoftDeletes]);

  // Fetch available tags of type 'Expertise'
  const expertises = useTags(TagTypes.Expertise);

  useEffect(() => {
    setNextDisabled(userExpertiseList.length === 0);
  }, [userExpertiseList, setNextDisabled]);

  useEffect(() => {
    registerOnBeforeNext(async () => {
      setIsProcessing(true);
      try {
        const updatedSoftDeletes = await saveFacadeTagNodes({
          items: userExpertiseList,
          softDeletes,
          userId: data.id,
          tagNodeCreate,
          tagNodeUpdate,
          tagNodeDestroy,
        });
        await refetchUser();
        updateData({ userExpertiseSoftDeletes: updatedSoftDeletes });
        setIsProcessing(false);
        return true;
      } catch (e) {
        console.error('Failed to save expertise', e);
        setIsProcessing(false);
        return false;
      }
    });
  }, [userExpertiseList, softDeletes, updateData, registerOnBeforeNext, tagNodeCreate, tagNodeUpdate, tagNodeDestroy, refetchUser, data.id, setIsProcessing]);

  /**
   * Toggles the selection of an expertise tag.
   *
   * @param {object} expertise - The expertise tag being toggled.
   */
  function handleChange(expertise) {
    const { items, softDeletes: newSoftDeletes } = toggleFacadeTagNode(userExpertiseList, softDeletes, expertise);
    setUserExpertiseList(items);
    setSoftDeletes(newSoftDeletes);
  }
  /**
   * Opens the dialog to edit details for a specific expertise.
   *
   * @param {object} expertise - The expertise tag.
   * @param {object} userExpertise - The user's expertise facade node.
   */
  function handleDetailsChange(expertise, userExpertise) {
    setEditingDialog({ expertise, userExpertise });
  }

  /**
   * Saves the updated description for an expertise.
   *
   * @param {string} description - The new description.
   */
  function handleDescriptionSave(description) {
    setUserExpertiseList((prev) => updateFacadeTagNodeDescription(prev, editingDialog.userExpertise._tag.id, description));
    setEditingDialog(null);
  }

  if (expertises.state === PayloadStates.FETCHING || loading) return (<Loader />)

  return (
    <div className="mt-6">
      <Overlay isVisible={saving || isProcessing}>
        {editingDialog && (
          <TagNodeUpdateDialog
            tag={editingDialog.expertise}
            tagNode={editingDialog.userExpertise._tagNode}
            onSave={handleDescriptionSave}
            onCancel={() => setEditingDialog(null)}
          />
        )}
        {/*<pre className="text-xs">{JSON.stringify(userExpertiseList, null, 1)}</pre>*/}
        {/*<pre className="text-xs">{JSON.stringify(softDeletes, null, 1)}</pre>*/}
        <FormTemplate
          title="What topics and experiences would you like to share with students and educators?"
          subtitle="Your experiences matter - whether they are professional or lived experiences, personal passions, or somewhere in-between. Select all that apply and we'll match you with the right volunteer opportunities."
          alert={error && (
            <RequestError request={request} v2={true} />
          )}
          body={(
            <ExpertiseSelector
              tags={expertises}
              facadeTagNodes={{ data: userExpertiseList}}
              onChange={handleChange}
              onUpdateDescription={handleDetailsChange}
            />
          )}
        />
      </Overlay>
    </div>
  );
}
